import React, { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { PaginationResponse } from '../../../common/types/pagination.types';
import {
  BookingType,
  bookingTypeTranslations,
} from '../Constants/BookingTypeEnum';
import { BookingListItem } from '../bookingsSlice';
import { useAppSelector } from '../../../app/hooks';
import { checkRole } from '../../auth/authSlice';
import { ROLES } from '../../../common/constants';

const PaginationComponent = lazy(() =>
  import('../../../common/components/PaginationComponent').then(module => ({
    default: module.PaginationComponent,
  })),
);

interface BookingsResultsTableProps {
  bookingItems: BookingListItem[];
  currentLanguage: 'en' | 'fi';
  bookingsPagination: PaginationResponse;
  onPaginationClick: (page: number) => void;
}

const formatBookingPeriod = (startDate: string, endDate: string) =>
  `${new Date(Date.parse(startDate)).toLocaleDateString('fi-FI', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })} - ${new Date(Date.parse(endDate)).toLocaleDateString('fi-FI', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })}`;

function getBookingTypeByName(name: string): BookingType | undefined {
  const bookingTypeNames = Object.keys(BookingType).filter(key =>
    Number.isNaN(Number(key)),
  );
  const foundType = bookingTypeNames.find(
    typeName => typeName.toLowerCase() === name.toLowerCase(),
  );

  return foundType
    ? BookingType[foundType as keyof typeof BookingType]
    : undefined;
}

function getBookingTypeTranslation(
  bookingType: string,
  locale: 'en' | 'fi',
): string {
  const selector = getBookingTypeByName(bookingType);

  return bookingTypeTranslations[locale][selector] || 'Unknown';
}

function BookingStatusBadge({
  item,
  wrapWithLink,
}: {
  item: BookingListItem;
  wrapWithLink: boolean;
}) {
  const { t } = useTranslation('bookings');

  let badgeVariant: 'danger' | 'primary' | 'info' = 'info';
  let badgeLabel = t('results.item.unconfirmed');

  if (item.isCancelled) {
    badgeVariant = 'danger';
    badgeLabel = t('results.item.cancelled');
  } else if (item.isConfirmed) {
    badgeVariant = 'primary';
    badgeLabel = t('results.item.confirmed');
  } else if (item.isEdited) {
    badgeVariant = 'danger';
    badgeLabel = t('results.item.inProgress');
  }

  if (!wrapWithLink) {
    return (
      <span className="d-block">
        <Badge bg={badgeVariant}>{badgeLabel}</Badge>
      </span>
    );
  }

  return (
    <Link
      className="d-block"
      to={`/bookings/${item.id}`}
      state={{
        backUrl: '/bookings/search-bookings',
      }}
    >
      <Badge bg={badgeVariant}>{badgeLabel}</Badge>
    </Link>
  );
}

function BookingTableRow({
  item,
  currentLanguage,
  isDomainUser,
}: {
  item: BookingListItem;
  currentLanguage: 'en' | 'fi';
  isDomainUser: boolean;
}) {
  const isTravelAgencyUser = useAppSelector(checkRole([ROLES.TravelAgency]));
  if (item.isEdited) {
    return (
      <tr key={item.id} className="bg-warning">
        <td>
          <span className="d-block">{item.bookingNumber}</span>
        </td>
        <td>
          <span className="d-block">
            {formatBookingPeriod(item.startDate, item.endDate)}
          </span>
        </td>
        <td>
          <span className="d-block">
            {getBookingTypeTranslation(item.bookingType, currentLanguage)}
          </span>
        </td>
        <td>
          <span className="d-block">
            {isTravelAgencyUser && item.finalClient
              ? item.finalClient
              : `${item.payerFirstName} ${item.payerLastName}`}
          </span>
        </td>
        <td>
          <span className="nav-link">
            {item.accommodationResponse.accommodationNumber}{' '}
            {item.accommodationResponse.accommodationName}
          </span>
        </td>
        <td>
          <span className="nav-link">{item.officerLastName}</span>
        </td>
        <td>
          <span className="nav-link">
            {new Date(item.createdAt).toLocaleDateString('fi-FI')}
          </span>
        </td>
        <td>
          <span className="nav-link">
            {item.updatedAt
              ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
              : ''}
          </span>
        </td>
        <td>
          <BookingStatusBadge item={item} wrapWithLink={false} />
        </td>
        {isDomainUser && <td>{item.travelAgency}</td>}
      </tr>
    );
  }

  return (
    <tr key={item.id}>
      <td>
        <Link
          className="d-block"
          to={`/bookings/${item.id}`}
          state={{ backUrl: '/bookings/search-bookings' }}
        >
          {item.bookingNumber}
          {item.restApiBookingId && ` / ${item.restApiBookingId}`}
        </Link>
      </td>
      <td>
        <Link
          className="d-block"
          to={`/bookings/${item.id}`}
          state={{ backUrl: '/bookings/search-bookings' }}
        >
          {formatBookingPeriod(item.startDate, item.endDate)}
        </Link>
      </td>
      <td>
        <Link
          className="d-block"
          to={`/bookings/${item.id}`}
          state={{ backUrl: '/bookings/search-bookings' }}
        >
          {getBookingTypeTranslation(item.bookingType, currentLanguage)}
        </Link>
      </td>
      <td>
        <Link
          className="d-block"
          to={`/bookings/${item.id}`}
          state={{ backUrl: '/bookings/search-bookings' }}
        >
          {isTravelAgencyUser && item.finalClient
            ? item.finalClient
            : `${item.payerFirstName} ${item.payerLastName}`}
        </Link>
      </td>
      <td>
        <Link
          className="nav-link"
          to={`/bookings/${item.id}`}
          state={{ backUrl: '/bookings/search-bookings' }}
        >
          {item.accommodationResponse.accommodationNumber}{' '}
          {item.accommodationResponse.accommodationName}
        </Link>
      </td>
      <td>
        <Link
          className="nav-link"
          to={`/bookings/${item.id}`}
          state={{ backUrl: '/bookings/search-bookings' }}
        >
          {item.officerLastName}
        </Link>
      </td>
      <td>
        <Link
          className="nav-link"
          to={`/bookings/${item.id}`}
          state={{ backUrl: '/bookings/search-bookings' }}
        >
          {new Date(item.createdAt).toLocaleDateString('fi-FI')}
        </Link>
      </td>
      <td>
        <span className="nav-link">
          {item.updatedAt
            ? new Date(item.updatedAt).toLocaleDateString('fi-FI')
            : ''}
        </span>
      </td>
      <td>
        <BookingStatusBadge item={item} wrapWithLink={false} />
      </td>
      {isDomainUser && <td>{item.travelAgency}</td>}
    </tr>
  );
}

export function BookingsResultsTable({
  bookingItems,
  currentLanguage,
  bookingsPagination,
  onPaginationClick,
}: BookingsResultsTableProps) {
  const { t } = useTranslation('bookings');
  const isTravelAgencyDomainUser = useAppSelector(
    checkRole([ROLES.TravelAgencyDomainUser]),
  );
  return (
    <Card className="border shadow-none">
      <div className="table-responsive-lg">
        <Table hover className="table-data">
          <thead>
            <tr>
              <th>{t('results.headers.id')}</th>
              <th>{t('results.headers.period')}</th>
              <th>{t('results.headers.type')}</th>
              <th>{t('results.headers.customer')}</th>
              <th>{t('results.headers.property')}</th>
              <th>{t('results.headers.clerk')}</th>
              <th>{t('results.headers.created')}</th>
              <th>{t('results.headers.modified')}</th>
              <th>{t('results.headers.status')}</th>
              {isTravelAgencyDomainUser && (
                <th>{t('results.headers.agent')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {bookingItems.map(item => (
              <BookingTableRow
                key={item.id}
                item={item}
                currentLanguage={currentLanguage}
                isDomainUser={isTravelAgencyDomainUser}
              />
            ))}
          </tbody>
        </Table>
        <Suspense fallback={<div>Ladataan...</div>}>
          <PaginationComponent
            OnPageClick={onPaginationClick}
            PaginationData={bookingsPagination}
          />
        </Suspense>
      </div>
    </Card>
  );
}
